/*
 * Convert Image to Chart — the capture → upload → extract chain.
 *
 * The convert endpoint reads its image from Azure Blob Storage, so one
 * extraction is three steps:
 *
 *   capture the selected picture's pixels  →  blob-sas + PUT  →  POST convert-image
 *
 * All three already exist — `slideImageCapture` and `blobImageUpload` are the
 * same modules Photo Headshot, Sketch to Slide and the chip classifier drive,
 * parameterised by messages and telemetry reasons. This module supplies the
 * chart-flavoured ones and joins them up.
 *
 * Unlike `classifySelection.ts`, failures here are NOT swallowed: this chain
 * runs because the user asked for a chart, so every failure must reach the
 * sheet as a rendered error rather than a quietly generic offer. Each step
 * therefore rejects with a message already fit to show.
 */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import {
  captureSelectedImage,
  readSelectedImageShape,
  type SlideImageMessages,
  type SlideImageReasons,
} from "../../shared/services/slideImageCapture";
import { uploadImageBlob, type BlobUploadResult } from "../../shared/services/blobImageUpload";
import { devTrace } from "../../shared/services/devTrace";
import {
  CONVERT_CHART_TRACE_SCOPE,
  logConvertChartError,
  postConvertChart,
  type ConvertChartResult,
} from "./convertChartApi";

/**
 * Selection copy. Every one of these can reach the sheet's error state, so
 * each says what the user should do rather than what the guard checked.
 */
export const CHART_PPT_ONLY_MESSAGE = "Convert to chart works only in PowerPoint.";
export const CHART_NO_SELECTION_MESSAGE = "Select the chart image on the slide, then try again.";
export const CHART_MULTI_SELECTION_MESSAGE = "Select just the chart image — more than one shape is selected.";
export const CHART_NOT_AN_IMAGE_MESSAGE = "Convert to chart needs a picture of a chart. Select an image and retry.";
export const CHART_CAPTURE_UNSUPPORTED_MESSAGE = "Reading the selected image needs PowerPointApi 1.5+.";

const MESSAGES: SlideImageMessages = {
  pptOnly: CHART_PPT_ONLY_MESSAGE,
  noSelection: CHART_NO_SELECTION_MESSAGE,
  multiSelection: CHART_MULTI_SELECTION_MESSAGE,
  notAnImage: CHART_NOT_AN_IMAGE_MESSAGE,
  captureUnsupported: CHART_CAPTURE_UNSUPPORTED_MESSAGE,
  itemLabel: "chart",
  // Fail fast on a malformed capture: uploading an empty blob would spend a
  // vision call reading nothing and come back as a confusing "not a chart".
  strictCapture: true,
};

/** One reason per distinct failure point, so a log line says which step broke. */
const REASONS: SlideImageReasons = {
  placeholderTypeCheck: "convert-chart-placeholder-type-check",
  slideSizeRead: "convert-chart-slide-size-read",
  slideCapture: "convert-chart-slide-capture",
  cropEncode: "convert-chart-crop-encode",
  cropEmptyResult: "convert-chart-crop-empty-result",
};

const UPLOAD_MESSAGES = {
  blobSasFailed: "Couldn't get a place to upload the chart image.",
  blobPutFailed: "Couldn't upload the chart image.",
};

const UPLOAD_REASONS = {
  formatSniff: "convert-chart-blob-format-sniff",
  sasRequest: "convert-chart-blob-sas-request",
  sasVersionParse: "convert-chart-blob-sas-version-parse",
  putEncode: "convert-chart-blob-put-encode",
  putNetwork: "convert-chart-blob-put-network",
  putBodyRead: "convert-chart-blob-put-body-read",
};

/** File name sent with the blob-sas request. The extension is re-derived from
 *  the bytes' own signature by the upload helper, so this is a label only. */
const UPLOAD_FILE_NAME = "convert-chart.png";

export interface ExtractChartConfig {
  readonly http: HttpClient;
  readonly clientId: number;
  /** Free-text steer forwarded as the request's `hint` (e.g. "values are in £m"). */
  readonly hint?: string | null;
  readonly options?: HttpRequestOptions;
}

/** An extraction, paired with the shape it was read from so the caller can
 *  tell a stale result (the user moved on to another picture) from a live one. */
export interface ChartExtraction {
  /** Office.js shape id of the picture the chart was read from. */
  readonly shapeId: string;
  readonly result: ConvertChartResult;
  readonly blob: BlobUploadResult;
}

/**
 * Capture, upload and convert the currently selected picture.
 *
 * Rejects — never resolves to a partial result. The caller renders the
 * rejection through `describeConvertChartFailure`.
 */
export async function extractChartFromSelection(config: ExtractChartConfig): Promise<ChartExtraction> {
  const options = config.options ?? {};

  devTrace(CONVERT_CHART_TRACE_SCOPE, "start", { clientId: config.clientId });

  const shape = await readSelectedImageShape(MESSAGES, logConvertChartError, REASONS);
  devTrace(CONVERT_CHART_TRACE_SCOPE, "read selected picture", { shapeId: shape.shapeId });

  const { base64, mimeType } = await captureSelectedImage(shape, MESSAGES, logConvertChartError, REASONS);
  // Length only — never the bytes themselves.
  devTrace(CONVERT_CHART_TRACE_SCOPE, "captured pixels", { mimeType, base64Length: base64.length });

  const blob = await uploadImageBlob({
    http: config.http,
    clientId: config.clientId,
    base64,
    mimeType,
    fileName: UPLOAD_FILE_NAME,
    options,
    messages: UPLOAD_MESSAGES,
    onError: logConvertChartError,
    reasons: UPLOAD_REASONS,
  });
  devTrace(CONVERT_CHART_TRACE_SCOPE, "uploaded blob", {
    blobName: blob.blobName,
    blobContainer: blob.blobContainer ?? "(api default)",
  });

  const result = await postConvertChart(config.http, blob, {
    ...(config.hint ? { hint: config.hint } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  return { shapeId: shape.shapeId, result, blob };
}

/**
 * Re-run only the vision call against an image already in blob storage.
 *
 * Used when the user supplies a description *after* the first extraction:
 * the pixels haven't changed, so re-capturing and re-uploading them would
 * spend two round trips to send the same bytes a second time.
 */
export async function reExtractChartWithHint(
  http: HttpClient,
  extraction: ChartExtraction,
  hint: string | null,
  options: HttpRequestOptions = {},
): Promise<ChartExtraction> {
  devTrace(CONVERT_CHART_TRACE_SCOPE, "re-extract with hint", { shapeId: extraction.shapeId, hasHint: Boolean(hint) });
  const result = await postConvertChart(http, extraction.blob, {
    ...(hint ? { hint } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });
  return { ...extraction, result };
}
